import api from './api';
import type { CredentialTypeSummary } from '@/types/jurisdiction';

export interface CreditProgressDto {
  jurisdictionId: number;
  jurisdictionCode: string;
  jurisdictionName: string;
  requirements: string | null;
  complianceDeadline: string | null;
  reportingDeadline: string | null;
  creditHourDefinition: string | null;
  specialRequirements: string | null;
  // Prefer credentialTypes for chip rendering — covers CLE/CPE/IAPP/EA and any
  // future admin-created types. The hasCle/hasCpe booleans are legacy.
  credentialTypes: CredentialTypeSummary[];
  hasCle: boolean;
  hasCpe: boolean;
  earnedCredits: { creditTypeName: string; amount: number }[];
  inProgressCredits: { creditTypeName: string; amount: number }[];
  registeredCredits: { creditTypeName: string; amount: number }[];
  requiredCredits: { creditTypeName: string; amount: number }[];
  compliancePeriodYears: number | null;
}

export interface CleAdvisorSearchResult {
  explanation: string;
  courses: unknown[];
}

export const cleAdvisorApi = {
  getSummary: async (): Promise<CreditProgressDto[]> => {
    const response = await api.get<CreditProgressDto[]>('/me/cle-advisor/summary');
    return response.data;
  },

  search: async (question: string, jurisdictionCode?: string, delivery?: string, accreditationType?: string): Promise<CleAdvisorSearchResult> => {
    const response = await api.post<CleAdvisorSearchResult>('/me/cle-advisor/search', {
      question,
      jurisdictionCode,
      delivery,
      accreditationType,
    });
    return response.data;
  },

  recommend: async (delivery?: string, accreditationType?: string): Promise<CleAdvisorSearchResult> => {
    const response = await api.post<CleAdvisorSearchResult>('/me/cle-advisor/recommend', {
      delivery,
      accreditationType,
    });
    return response.data;
  },

  /** Trigger background pre-fetch of recommendations. Returns immediately. */
  prefetch: async (): Promise<void> => {
    await api.post('/me/cle-advisor/prefetch');
  },

  /** Get cached recommendations (pre-computed). Returns null if not cached yet. */
  getCached: async (): Promise<CleAdvisorSearchResult | null> => {
    const response = await api.get('/me/cle-advisor/recommendations', {
      validateStatus: (status: number) => status === 200 || status === 204,
    });
    if (response.status === 204) return null;
    return response.data;
  },
};
